/**
 * DeveloperRequestDetail Component
 *
 * Full management interface for feature requests (admin-only).
 * Allows status changes, priority updates, notes, and responses.
 *
 * @module components/dev/DeveloperRequestDetail
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Dialog,
  DialogContent,
  Stack,
  Chip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useUser } from '../../contexts/AuthContext';
import { FeatureRequestService } from '../../services/featureRequestService';
import CommentThread from '../feedback/CommentThread';
import {
  FeatureRequest,
  RequestStatus,
  DeveloperPriority,
  STATUS_BADGES,
  REQUEST_TYPES,
  USER_PRIORITIES,
  DEVELOPER_PRIORITIES,
} from '../../types/featureRequest';
import { formatDistanceToNow } from 'date-fns';

/**
 * DeveloperRequestDetail - Full management interface
 */
const DeveloperRequestDetail: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const user = useUser();
  const navigate = useNavigate();

  // State
  const [request, setRequest] = useState<FeatureRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Form state
  const [status, setStatus] = useState<RequestStatus>('new');
  const [developerPriority, setDeveloperPriority] = useState<DeveloperPriority | ''>('');
  const [developerNotes, setDeveloperNotes] = useState('');
  const [publicResponse, setPublicResponse] = useState('');
  const [milestone, setMilestone] = useState('');

  /**
   * Fetch request
   */
  useEffect(() => {
    if (requestId) {
      fetchRequest();
    }
  }, [requestId]);

  const fetchRequest = async () => {
    if (!requestId) return;

    try {
      setLoading(true);
      setError(null);

      const fetchedRequest = await FeatureRequestService.getRequestById(requestId);

      if (!fetchedRequest) {
        setError('Request not found');
        return;
      }

      setRequest(fetchedRequest);
      setStatus(fetchedRequest.status);
      setDeveloperPriority(fetchedRequest.developerPriority || '');
      setDeveloperNotes(fetchedRequest.developerNotes || '');
      setPublicResponse(fetchedRequest.publicResponse || '');
      setMilestone(fetchedRequest.assignedToMilestone || '');
    } catch (err: any) {
      console.error('Error fetching request:', err);
      setError(err.message || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle save all changes
   */
  const handleSave = async () => {
    if (!request || !requestId) return;

    try {
      setSaving(true);
      setError(null);

      const userId = user?.uid || 'admin';
      const userName = user?.displayName || 'Admin';

      // Update status if changed
      if (status !== request.status) {
        await FeatureRequestService.updateRequestStatus(requestId, status, userId, userName);
      }

      // Update developer priority if changed
      if (developerPriority && developerPriority !== request.developerPriority) {
        await FeatureRequestService.updateDeveloperPriority(
          requestId,
          developerPriority,
          userId,
          userName
        );
      }

      // Update developer notes if changed
      if (developerNotes !== (request.developerNotes || '')) {
        await FeatureRequestService.addDeveloperNote(requestId, developerNotes, userId, userName);
      }

      // Update public response if changed
      if (publicResponse !== (request.publicResponse || '')) {
        await FeatureRequestService.addPublicResponse(
          requestId,
          publicResponse,
          userId,
          userName
        );
      }

      // Update milestone if changed
      if (milestone !== (request.assignedToMilestone || '')) {
        await FeatureRequestService.assignToMilestone(requestId, milestone, userId, userName);
      }

      setSuccess('Changes saved successfully');
      await fetchRequest();
    } catch (err: any) {
      console.error('Error saving changes:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    navigate('/dev/feedback-dashboard');
  };

  /**
   * Format date
   */
  const formatDate = (dateString: string): string => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  /**
   * Open image dialog
   */
  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  /**
   * Close image dialog
   */
  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  // Check if user is admin
  if (user && user.role !== 'admin') {
    return (
      <Box maxWidth={800} mx="auto" p={3}>
        <Alert severity="error">
          Access Denied: You must be an administrator to access this page.
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error && !request) {
    return (
      <Box maxWidth={1200} mx="auto" p={3}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={handleBack} startIcon={<BackIcon />} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  if (!request) {
    return null;
  }

  const typeConfig = REQUEST_TYPES[request.type];
  const userPriorityConfig = USER_PRIORITIES[request.userPriority];
  const hasChanges =
    status !== request.status ||
    developerPriority !== (request.developerPriority || '') ||
    developerNotes !== (request.developerNotes || '') ||
    publicResponse !== (request.publicResponse || '') ||
    milestone !== (request.assignedToMilestone || '');

  return (
    <Box maxWidth={1400} mx="auto" p={3}>
      {/* Back Button */}
      <Button startIcon={<BackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
        Back to Dashboard
      </Button>

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3 }}>
            {/* Header */}
            <Stack direction="row" spacing={1} mb={2}>
              <Chip
                label={STATUS_BADGES[request.status].label}
                sx={{
                  bgcolor: STATUS_BADGES[request.status].color,
                  color: 'white',
                }}
              />
              <Chip label={`${typeConfig.icon} ${typeConfig.label}`} variant="outlined" />
            </Stack>

            <Typography variant="h4" gutterBottom>
              {request.title}
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={3}>
              Submitted {formatDate(request.createdAt)} by {request.submittedByName || 'Anonymous'}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Description */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {request.description}
              </Typography>
            </Box>

            {/* Screenshots */}
            {request.screenshots.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Screenshots ({request.screenshots.length})
                </Typography>
                <Grid container spacing={2}>
                  {request.screenshots.map((screenshot) => (
                    <Grid item xs={12} sm={6} md={4} key={screenshot.id}>
                      <Paper
                        elevation={1}
                        sx={{
                          cursor: 'pointer',
                          overflow: 'hidden',
                          '&:hover': { elevation: 4 },
                        }}
                        onClick={() => handleImageClick(screenshot.url)}
                      >
                        <Box
                          component="img"
                          src={screenshot.url}
                          alt={screenshot.fileName}
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover',
                          }}
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Comments */}
            <CommentThread requestId={request.id} isAdmin />
          </Paper>
        </Grid>

        {/* Management Sidebar */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Status Management */}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Status Management
              </Typography>

              <TextField
                select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value as RequestStatus)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              >
                {Object.entries(STATUS_BADGES).map(([value, config]) => (
                  <MenuItem key={value} value={value}>
                    {config.icon} {config.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Developer Priority"
                value={developerPriority}
                onChange={(e) => setDeveloperPriority(e.target.value as DeveloperPriority)}
                fullWidth
                size="small"
              >
                <MenuItem value="">None</MenuItem>
                {Object.entries(DEVELOPER_PRIORITIES).map(([value, config]) => (
                  <MenuItem key={value} value={value}>
                    {config.label}
                  </MenuItem>
                ))}
              </TextField>
            </Paper>

            {/* Request Info */}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Request Information
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    User Priority
                  </Typography>
                  <Chip
                    label={userPriorityConfig.label}
                    size="small"
                    sx={{
                      bgcolor: userPriorityConfig.color,
                      color: 'white',
                      mt: 0.5,
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">
                    {request.submittedByEmail || 'Not provided'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Screenshots
                  </Typography>
                  <Typography variant="body2">{request.screenshotCount}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Comments
                  </Typography>
                  <Typography variant="body2">{request.commentCount}</Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Developer Notes (Private) */}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Developer Notes (Private)
              </Typography>
              <TextField
                multiline
                rows={4}
                value={developerNotes}
                onChange={(e) => setDeveloperNotes(e.target.value)}
                fullWidth
                placeholder="Add private notes visible only to developers..."
                size="small"
              />
            </Paper>

            {/* Public Response */}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Public Response
              </Typography>
              <TextField
                multiline
                rows={4}
                value={publicResponse}
                onChange={(e) => setPublicResponse(e.target.value)}
                fullWidth
                placeholder="Add a response visible to the user..."
                size="small"
              />
            </Paper>

            {/* Milestone */}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Milestone
              </Typography>
              <TextField
                value={milestone}
                onChange={(e) => setMilestone(e.target.value)}
                fullWidth
                placeholder="e.g., v2.5.0"
                size="small"
              />
            </Paper>

            {/* Save Button */}
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges || saving}
              fullWidth
              size="large"
            >
              {saving ? 'Saving...' : 'Save All Changes'}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Image Viewer Dialog */}
      <Dialog open={!!selectedImage} onClose={handleCloseImage} maxWidth="lg" fullWidth>
        <IconButton
          onClick={handleCloseImage}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Screenshot"
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DeveloperRequestDetail;
